// =============================================================================
// Origin UI — src/components/common/index.tsx
// Reusable UI components styled per the OQT Origin Style Guide.
// =============================================================================

import { type ReactNode } from 'react';
import type { RequestStatus, TaskItemStatus } from '@/types/api';
import styles from './common.module.css';

// ─── Status Badge ───

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  Draft:          { bg: 'var(--gray-100)',        text: 'var(--gray-600)',    dot: 'var(--gray-400)' },
  Submitted:      { bg: 'var(--purple-light)',    text: 'var(--purple)',      dot: 'var(--purple)' },
  Returned:       { bg: 'var(--orange-light)',    text: 'var(--orange)',      dot: 'var(--orange)' },
  Approved:       { bg: 'var(--turquoise-light)', text: 'var(--turquoise-dark)', dot: 'var(--turquoise)' },
  TasksInProgress:{ bg: '#EBF5FF',               text: '#2563EB',           dot: '#3B82F6' },
  Completed:      { bg: 'var(--green-light)',     text: 'var(--green)',      dot: 'var(--green)' },
  Cancelled:      { bg: 'var(--red-light)',       text: 'var(--red)',        dot: 'var(--red)' },
  // Task statuses
  Pending:        { bg: 'var(--gray-100)',        text: 'var(--gray-600)',    dot: 'var(--gray-400)' },
  InProgress:     { bg: '#EBF5FF',               text: '#2563EB',           dot: '#3B82F6' },
  Blocked:        { bg: 'var(--red-light)',       text: 'var(--red)',        dot: 'var(--red)' },
};

const statusLabels: Record<string, string> = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  Returned: 'Returned',
  Approved: 'Approved',
  TasksInProgress: 'Tasks In Progress',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
  Pending: 'Pending',
  InProgress: 'In Progress',
  Blocked: 'Blocked',
};

export function StatusBadge({ status }: { status: RequestStatus | TaskItemStatus | string }) {
  const colors = statusColors[status] || statusColors.Draft;
  const label = statusLabels[status] || status;

  return (
    <span
      className={styles.statusBadge}
      style={{ background: colors.bg, color: colors.text }}
    >
      <span className={styles.statusDot} style={{ background: colors.dot }} />
      {label}
    </span>
  );
}

// ─── Loading Spinner ───

export function LoadingSpinner({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.spinner} />
      <p className={styles.loadingText}>{message}</p>
    </div>
  );
}

// ─── Empty State ───

export function EmptyState({
  icon = '📋',
  title,
  message,
  action,
}: {
  icon?: string;
  title: string;
  message: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon}>{icon}</span>
      <h3 className={styles.emptyTitle}>{title}</h3>
      <p className={styles.emptyMessage}>{message}</p>
      {action && <div className={styles.emptyAction}>{action}</div>}
    </div>
  );
}

// ─── Confirm Dialog ───

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  confirmVariant = 'danger',
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.dialogTitle}>{title}</h3>
        <p className={styles.dialogMessage}>{message}</p>
        {children}
        <div className={styles.dialogActions}>
          <button className={styles.btnSecondary} onClick={onCancel}>
            Cancel
          </button>
          <button
            className={confirmVariant === 'danger' ? styles.btnDanger : styles.btnPrimary}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Toast / Error Banner ───

export function ErrorBanner({ message, onDismiss }: { message: string; onDismiss?: () => void }) {
  return (
    <div className={styles.errorBanner}>
      <span>{message}</span>
      {onDismiss && (
        <button className={styles.errorDismiss} onClick={onDismiss}>✕</button>
      )}
    </div>
  );
}

// ─── Page Header ───

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className={styles.pageHeader}>
      <div>
        <h1 className={styles.pageTitle}>{title}</h1>
        {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ─── Card ───

export function Card({
  children,
  className,
  padding = true,
}: {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div className={`${styles.card} ${padding ? styles.cardPadded : ''} ${className || ''}`}>
      {children}
    </div>
  );
}
